import React, { useCallback, useState, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Actionable, Text, SheetContainer, InputRadio, Bubble, ActionBar, useTheme, Divider, Title, Button, Stack, Icon, useViewport } from "@bookingcom/bui-react";
import { SortIcon, FilterFunnelIcon } from "@bookingcom/bui-assets-react/streamline";
import { useI18n, Trans } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { useActions } from "../../../store/index.js";
import { actions as searchActions } from "../../../store/search/actions.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import useSearchFilters from "../../../hooks/useSearchFilters.js";
import useModalDialog from "../../../hooks/useModalDialog.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import useEventTracking from "../../../hooks/useEventTracking.js";
import Frame from "../Frame/index.js";
import styles from "./SearchToolbar.module.css";
import SearchFiltersMobile from "../SearchFilters/SearchFilters.mobile.js";
import { useGetFiltersDraft } from "../SearchResults/components/boundsFilterHooks.js";
import useUserAgent from "../../../hooks/useUserAgent.js";
import { useAppliedFilters } from "../../../hooks/search/useAppliedFilters.js";
import { useSelector } from "react-redux";
import { getSearchAvailableFilters } from "../../../store/searchResults/selectors.js";
const SearchToolbar = () => {
    const { enhanceURLSearchParams } = useCreateUrl();
    const history = useHistory();
    const location = useLocation();
    const { applyFilters } = useSearchFilters();
    const { isLarge } = useViewport();
    const { isMobile } = useUserAgent();
    const actions = useActions(searchActions);
    const initialSort = useMemo(() => {
        const query = new URLSearchParams(location.search);
        return query.get("sort") || "BEST";
    }, [location.search]);
    const [sortSelected, setSortSelected] = useState(initialSort);
    const [sortModified, setSortModified] = useState(false);
    const onSortRadioButtonChange = useCallback(({ id }) => {
        setSortModified(id !== initialSort);
        setSortSelected(id);
        actions.setSortOrder(id);
    }, [actions, initialSort]);
    const onSortModalClose = useCallback(() => {
        if (!sortModified) {
            return;
        }
        const params = new URLSearchParams(location.search);
        params.set("sort", sortSelected);
        params.delete("page");
        params.delete("auto_load_page");
        enhanceURLSearchParams(params);
        history.replace({
            pathname: location.pathname,
            search: params.toString()
        });
    }, [enhanceURLSearchParams, history, location.pathname, location.search, sortModified, sortSelected]);
    const filtersDraftWithBounds = useGetFiltersDraft();
    const onFiltersModalClose = useCallback(() => {
        applyFilters(filtersDraftWithBounds);
    }, [applyFilters, filtersDraftWithBounds]);
    const isStuck = window.scrollY >= 72;
    return (React.createElement("div", { className: `${styles.wrapper} ${!isMobile && !isLarge ? styles.wrapperRes : ""}` },
        React.createElement(Frame, { direction: "row", justifyContent: "space-around", className: `${styles.toolbar} ${!isMobile && !isLarge ? styles.toolbarRes : ""}` },
            React.createElement(SortItem, { sortSelected: sortSelected, onChange: onSortRadioButtonChange, onClose: onSortModalClose }),
            React.createElement(FiltersItem, { onClose: onFiltersModalClose })),
        isStuck && React.createElement(Divider, null)));
};
const FiltersItem = ({ onClose }) => {
    const i18n = useI18n();
    const availableFilters = useSelector(getSearchAvailableFilters);
    const { requestId } = useGlobalContext();
    const trackV2 = useEventTracking("search_results", requestId);
    const { filteredTotalCount = 0, totalCount = 0 } = availableFilters;
    const appliedFilters = useAppliedFilters();
    const [isActive, setIsActive] = useState(false);
    const Modal = useModalDialog();
    const handleClose = useCallback(() => {
        setIsActive(false);
        onClose?.();
    }, [onClose]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ToolbarItem, { iconSVG: FilterFunnelIcon, text: i18n.trans(t("flights_filter_button")), number: appliedFilters.length, onClick: () => {
                trackV2("click_filters");
                setIsActive(true);
            }, ariaLabel: i18n.trans(t("flights_filter_button")) }),
        React.createElement(Modal, { active: isActive, onClose: handleClose, fullHeight: true, hideClose: true, footer: React.createElement(ActionBar, { fillEqually: true, button: { text: i18n.trans(t("flights_action_done")), onClick: handleClose, size: "large" } },
                React.createElement(Title, { title: i18n.trans(t("flights_num_search_filters_applied", {
                        num_exception: appliedFilters.length,
                        variables: { num_filters: String(appliedFilters.length) }
                    })), subtitle: appliedFilters.length > 0
                        ? i18n.trans(t("flights_number_filtered_results", {
                            num_exception: totalCount,
                            variables: { num_results: String(filteredTotalCount), num_total_results: String(totalCount) }
                        }))
                        : i18n.trans(t("flights_number_total_results", {
                            num_exception: filteredTotalCount,
                            variables: { num_results: String(totalCount) }
                        })) })), fill: true },
            React.createElement("div", { className: styles.modalContent },
                React.createElement(Frame, { mt: 3, mb: 3 },
                    React.createElement(Text, { tagName: "h2", variant: "headline_1" }, i18n.trans(t("flights_filter_name")))),
                React.createElement(SearchFiltersMobile, null)))));
};
const SortItem = ({ sortSelected, onChange, onClose }) => {
    const i18n = useI18n();
    const [isActive, setIsActive] = useState(false);
    const handleClose = useCallback(() => {
        setIsActive(false);
        onClose?.();
    }, [onClose]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ToolbarItem, { iconSVG: SortIcon, text: i18n.trans(t("flights_filter_sort")), onClick: () => {
                setIsActive(true);
            }, ariaLabel: i18n.trans(t("flights_filter_sort")) }),
        React.createElement(SheetContainer, { title: i18n.trans(t("flights_filter_sort")), closeAriaLabel: i18n.trans(t("flights_a11y_sort_close")), active: isActive, onCloseTrigger: handleClose, position: "bottom", footer: React.createElement(Stack, null,
                React.createElement(Divider, null),
                React.createElement(Stack, { className: styles.button },
                    React.createElement("div", { "data-testid": "search_toolbar_sort_modal_done" },
                        React.createElement(Button, { text: i18n.trans(t("flights_action_done")), wide: true, size: "large", onClick: handleClose })))) },
            React.createElement(SortRadioList, { sortSelected: sortSelected, onChange: onChange }))));
};
const SortRadioList = ({ sortSelected, onChange }) => {
    const i18n = useI18n();
    const theme = useTheme();
    return (React.createElement(React.Fragment, null,
        React.createElement(Frame, { mb: 4 },
            React.createElement(SortRadioButton, { label: i18n.trans(t("flights_search_sort_best")), id: "BEST", checked: sortSelected === "BEST", onChange: onChange },
                React.createElement(Frame, { attributes: { style: { paddingLeft: "29px", color: theme.colors.color_foreground_alt } } },
                    React.createElement(Text, { variant: "small_1" },
                        React.createElement(Trans, { tag: "flights_search_sort_best_tooltip" }))))),
        React.createElement(Frame, { mb: 6 },
            React.createElement(SortRadioButton, { label: i18n.trans(t("flights_search_sort_cheapest")), id: "CHEAPEST", checked: sortSelected === "CHEAPEST", onChange: onChange })),
        React.createElement(Frame, { mb: 3 },
            React.createElement(SortRadioButton, { label: i18n.trans(t("flights_search_sort_fastest")), id: "FASTEST", checked: sortSelected === "FASTEST", onChange: onChange }))));
};
const SortRadioButton = ({ label, id, checked, onChange, children }) => {
    return (React.createElement("div", { onClick: () => {
            onChange?.({ id, checked: true });
        }, role: "radio", "aria-checked": checked, tabIndex: 0 },
        React.createElement(InputRadio, { label: label, name: id, key: id, checked: checked, attributes: { "data-testid": `search_toolbar_radio_${label}` } }),
        children));
};
const ToolbarItem = ({ iconSVG, text, number, onClick, ariaLabel }) => {
    return (React.createElement(Actionable, { onClick: onClick, attributes: {
            "aria-label": ariaLabel,
            style: {
                width: "100%",
                justifyContent: "center",
                alignItems: "center"
            },
            "data-testid": `search_toolbar_${ariaLabel}`
        } },
        React.createElement(Stack, { direction: "row", alignItems: "center" },
            React.createElement(Icon, { svg: iconSVG, size: "small" }),
            React.createElement(Text, { variant: "body_2" }, text),
            number && number > 0 ? React.createElement(Bubble, { text: number, ariaLabel: `${number} applied filters` }) : null)));
};
export default SearchToolbar;
